import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Clock, Coffee, CalendarOff, Hourglass, LogOut, Send, CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useShiftFreeze } from "@/context/ShiftFreezeContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const REASON_META = {
  weekend: { icon: CalendarOff, title: "Today is a non-working day", tone: "bg-amber-100 text-amber-800" },
  before_shift: { icon: Clock, title: "Shift hasn't started yet", tone: "bg-sky-100 text-sky-800" },
  shift_ended: { icon: Lock, title: "Your shift has ended", tone: "bg-rose-100 text-rose-800" },
  break_window: { icon: Coffee, title: "Scheduled break in progress", tone: "bg-amber-100 text-amber-800" },
  voluntary_break: { icon: Coffee, title: "You're on a voluntary break", tone: "bg-amber-100 text-amber-800" },
};

const fmtTime = (iso) => {
  try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};

export default function ShiftFreezeOverlay() {
  const { status, refresh } = useShiftFreeze();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [requesting, setRequesting] = useState(false);
  const [reason, setReason] = useState("");
  const [endingBreak, setEndingBreak] = useState(false);

  const ext = status?.extension;
  const reasonKey = status?.reason;
  const wasPending = ext?.status === "pending";

  // Auto-poll for changes is handled in context; we just react to status here.
  // Fire approval toast EXACTLY once when extension flips from pending → active.
  useEffect(() => {
    if (ext?.active && wasPending) {
      toast.success(`Extension approved — ${ext.minutes_left} minutes added.`);
    }
  }, [ext?.active, ext?.minutes_left, wasPending]);

  if (!status || !status.frozen) return null;

  const meta = REASON_META[reasonKey] || { icon: Lock, title: status.reason_text || "App locked", tone: "bg-rose-100 text-rose-800" };
  const Icon = meta.icon;

  const requestExtension = async () => {
    setRequesting(true);
    try {
      await api.post("/shift-freeze/extensions/request", { reason: reason.trim() });
      toast.success("Extension request sent. Waiting for admin approval.");
      setReason("");
      await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to request extension");
    } finally {
      setRequesting(false);
    }
  };

  const endVoluntaryBreak = async () => {
    setEndingBreak(true);
    try {
      await api.post("/shift-freeze/voluntary-break/end");
      toast.success("Break ended. Welcome back.");
      await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to end break");
    } finally {
      setEndingBreak(false);
    }
  };

  const handleLogout = async () => {
    try { await logout(); } catch { /* noop */ }
    navigate("/login");
  };

  const showExtensionFlow = reasonKey === "shift_ended";

  return (
    <div
      data-testid="shift-freeze-overlay"
      className="fixed inset-0 z-[9999] bg-black/85 backdrop-blur-sm flex items-center justify-center px-4 py-8 overflow-y-auto"
    >
      <div className="bg-background border border-border rounded-lg shadow-2xl w-full max-w-lg p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-4">
          <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full ${meta.tone}`}>
            <Icon size={22} strokeWidth={2} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">App Locked</div>
            <h2 className="font-display text-xl font-bold leading-tight" data-testid="freeze-title">{meta.title}</h2>
          </div>
        </div>

        <p className="text-sm text-muted-foreground mb-4" data-testid="freeze-reason-text">
          {status.reason_text}
        </p>

        {status.schedule && (
          <div className="border border-border rounded-sm p-3 mb-4 bg-secondary/30 text-xs space-y-1">
            <div className="flex justify-between"><span className="text-muted-foreground">Your shift</span><span className="font-mono">{status.schedule.start_time} – {status.schedule.end_time}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Timezone</span><span className="font-mono">{status.timezone}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Now</span><span className="font-mono">{fmtTime(status.now_local)}</span></div>
            {(status.schedule.break_windows || []).length > 0 && (
              <div className="flex justify-between"><span className="text-muted-foreground">Break</span><span className="font-mono">{status.schedule.break_windows.map(b => `${b.start}–${b.end}`).join(", ")}</span></div>
            )}
          </div>
        )}

        {/* Voluntary break — primary action is to end the break */}
        {reasonKey === "voluntary_break" && (
          <Button
            data-testid="end-voluntary-break-btn"
            onClick={endVoluntaryBreak}
            disabled={endingBreak}
            className="w-full rounded-sm mb-3"
          >
            {endingBreak ? "Resuming…" : "I'm back — end break"}
          </Button>
        )}

        {/* Shift-ended path: request extension OR show pending/approved state */}
        {showExtensionFlow && (
          <div className="border-t border-border pt-4 mt-2">
            {ext?.active ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 text-sm flex items-start gap-2" data-testid="extension-active-block">
                <CheckCircle2 size={16} className="text-emerald-700 mt-0.5" />
                <div>
                  <div className="font-medium text-emerald-800">Extension granted</div>
                  <div className="text-emerald-700/80 text-xs mt-0.5">{ext.minutes_left} minutes left — page will auto-unlock.</div>
                </div>
              </div>
            ) : ext?.status === "pending" ? (
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-sm flex items-start gap-2" data-testid="extension-pending-block">
                <Hourglass size={16} className="text-amber-700 mt-0.5 animate-pulse" />
                <div>
                  <div className="font-medium text-amber-800">Waiting for admin approval</div>
                  <div className="text-amber-700/80 text-xs mt-0.5">We&apos;ll auto-refresh every 15 seconds.</div>
                </div>
              </div>
            ) : ext?.used_today ? (
              <div className="bg-slate-50 border border-border rounded-sm p-3 text-sm" data-testid="extension-used-block">
                <div className="font-medium">Extension already used today</div>
                <div className="text-muted-foreground text-xs mt-0.5">One 20-minute extension per shift. Please log out and resume tomorrow.</div>
              </div>
            ) : (
              <div className="space-y-2" data-testid="extension-request-block">
                <div className="text-xs text-muted-foreground">
                  Need a few more minutes to wrap up? Request a one-time <strong>20-minute</strong> add-on. An admin will approve inline.
                </div>
                <Textarea
                  data-testid="extension-reason-input"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Reason for extension (optional)…"
                  rows={2}
                  className="text-xs rounded-sm"
                />
                <Button
                  data-testid="extension-request-btn"
                  onClick={requestExtension}
                  disabled={requesting}
                  className="w-full rounded-sm"
                >
                  <Send size={14} className="mr-1" />
                  {requesting ? "Requesting…" : "Request 20-min extension"}
                </Button>
              </div>
            )}
          </div>
        )}

        <div className="mt-5 flex items-center justify-between gap-2 border-t border-border pt-3">
          <div className="text-[10px] text-muted-foreground font-mono">
            {status.role_label && <>Role: <span className="font-medium">{status.role_label}</span></>}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleLogout}
            data-testid="freeze-logout-btn"
            className="rounded-sm"
          >
            <LogOut size={12} className="mr-1" /> Sign out
          </Button>
        </div>
      </div>
    </div>
  );
}
